import React, { useState, useEffect } from "react";
import { BaseModal } from "./BaseModal";
import { TextInput, SelectInput, TextArea } from "./InputGroup";

interface EmailModalProps {
    open: boolean;
    onClose: () => void;
    onOpen: () => void;
    leftOffset?: number;
    sender?: string;
    onEmailSent: (activity: { icon: string; time: string; text: string }) => void;
}

interface Template {
    stage: string;
    subject: string;
    body: string;
}

const templateOptions: Template[] = [
    {
        stage: "Anfrage erhalten",
        subject: "Bestätigung: Ihre Anfrage bei uns",
        body: "Sehr geehrte Damen und Herren,\n\nvielen Dank für Ihre Anfrage. Wir werden uns in Kürze bei Ihnen melden.\n\nMit freundlichen Grüßen\nIhr Team",
    },
    {
        stage: "Termin vereinbart",
        subject: "Terminbestätigung",
        body: `Sehr geehrter Herr Mustermann,

vielen Dank für das angenehme Telefonat und Ihr Interesse an unserem Pflegedienst. 

Wie besprochen möchten wir Ihnen gerne persönlich weiterhelfen und bestätigen hiermit unseren Termin:

Datum: XX.XX.XXXX
Uhrzeit: XX:XX Uhr

Im Rahmen dieses Termins werden wir Ihre konkreten Pflegebedürfnisse vor Ort besprechen, gemeinsam die nächsten Schritte planen und Ihnen unser Team vorstellen.

Falls Sie Unterlagen (wie z. B. Medikamentenpläne, Pflegegradbescheid o. Ä.) bereits vorliegen haben, können Sie diese gerne schon bereitlegen. 

Sollten Sie den Termin nicht wahrnehmen können, bitten wir um eine kurze Nachricht.

Ich freue mich auf unser Gespräch und stehe bei Rückfragen selbstverständlich gern zur Verfügung.

Mit freundlichen Grüßen,
Peter Meier`,
    },
    {
        stage: "Termin bestätigt",
        subject: "Terminbestätigung",
        body: `Sehr geehrter Herr Mustermann,

vielen Dank für das angenehme Telefonat und Ihr Interesse an unserem Pflegedienst. 

Wie besprochen möchten wir Ihnen gerne persönlich weiterhelfen und bestätigen hiermit unseren Termin:

Datum: XX.XX.XXXX
Uhrzeit: XX:XX Uhr

Im Rahmen dieses Termins werden wir Ihre konkreten Pflegebedürfnisse vor Ort besprechen, gemeinsam die nächsten Schritte planen und Ihnen unser Team vorstellen.

Falls Sie Unterlagen (wie z. B. Medikamentenpläne, Pflegegradbescheid o. Ä.) bereits vorliegen haben, können Sie diese gerne schon bereitlegen. 

Sollten Sie den Termin nicht wahrnehmen können, bitten wir um eine kurze Nachricht.

Ich freue mich auf unser Gespräch und stehe bei Rückfragen selbstverständlich gern zur Verfügung.

Mit freundlichen Grüßen,
Peter Meier`,
    },
    {
        stage: "Telefoniert",
        subject: "Zusammenfassung unseres Telefonats",
        body: "Hallo,\n\nvielen Dank für das freundliche Telefonat. Hiermit fasse ich die besprochenen Punkte kurz zusammen:\n\n- Punkt 1\n- Punkt 2\n\nMit freundlichen Grüßen\nIhr Team",
    },
];

const EmailModal: React.FC<EmailModalProps> = ({
    open,
    onClose,
    onOpen,
    leftOffset = 50,
    sender = "peter.meier@pflegesorgenlos.de",
    onEmailSent,
}) => {
    const [recipient, setRecipient] = useState("max.mustermann@gmail.com");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [error, setError] = useState("");
    const [selectedRelative, setSelectedRelative] = useState<string>("");


    const relativeItems = [
        {
            title: "Petra Mustermann",
            detail: "Mutter - Primärer Kontakt",
        },
        {
            title: "Hans Mustermann",
            detail: "Bruder - Sekundärer Kontakt",
        },
    ];


    // Zustände für das neue Template-Popup
    const [isNewTemplateModalOpen, setIsNewTemplateModalOpen] = useState(false);
    const [newTemplateSubject, setNewTemplateSubject] = useState("");
    const [newTemplateBody, setNewTemplateBody] = useState("");

    useEffect(() => {
        if (selectedTemplate) {
            const template = templateOptions.find((t) => t.stage === selectedTemplate);
            if (template) {
                setSubject(template.subject);
                setBody(template.body);
            }
        }
    }, [selectedTemplate]);

    const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleSend = () => {
        if (!validateEmail(recipient)) {
            setError("Bitte eine gültige Empfängeradresse eingeben");
            return;
        }

        const newActivity = {
            icon: "fa-solid fa-envelope",
            time: new Date().toLocaleTimeString("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }),
            detail: new Date().toLocaleTimeString("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }),
            title: subject,
            text: `E-Mail an Max Mustermann gesendet – Betreff: ${subject}`,
        };

        onEmailSent(newActivity);
        [setSubject, setBody, setSelectedTemplate, setError].forEach(fn => fn(""));
        onClose();
    };

    // Funktion, um den neuen Template-Popup zu speichern
    const handleSaveNewTemplate = () => {
        if (newTemplateSubject.trim() && newTemplateBody.trim()) {
            setSubject(newTemplateSubject);
            setBody(newTemplateBody);
            // Optional: ausgewählte Vorlage leeren
            setSelectedTemplate("");
            // Popup schließen und Felder zurücksetzen
            setIsNewTemplateModalOpen(false);
            setNewTemplateSubject("");
            setNewTemplateBody("");
        }
    };

    return (
        <>
            <BaseModal
                open={open}
                onClose={onClose}
                onOpen={onOpen}
                leftOffset={leftOffset}
                headerText="E-Mail senden"
                className="email-modal"
            >
                <div className="input-group">
                    <label>Empfänger auswählen</label>
                    <select
                        value={selectedRelative}
                        onChange={(e) => setSelectedRelative(e.target.value)}
                    >
                        <option value="">Max Mustermann - Patient selbst</option>
                        {relativeItems.map((relative, idx) => (
                            <option key={idx} value={relative.title}>
                                {relative.title} - {relative.detail}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center" }} >
                    <SelectInput
                        label="Vorlage wählen:"
                        value={selectedTemplate}
                        onChange={setSelectedTemplate}
                        options={['', ...templateOptions.map(t => t.stage)]}
                        placeholder="-- Vorlage wählen --"
                    />
                    <button
                        className="template-button"
                        style={{ marginLeft: "10px" }}
                        onClick={() => setIsNewTemplateModalOpen(true)}
                    >
                        Neue Vorlage erstellen
                    </button>
                </div>

                <TextInput
                    label="Betreff:"
                    value={subject}
                    onChange={setSubject}
                    placeholder="Betreff eingeben"
                />

                <TextArea
                    label="Nachricht:"
                    value={body}
                    onChange={setBody}
                    placeholder="Nachricht eingeben..."
                    minHeight={200}
                />

                <div className="sender-info">
                    <TextInput
                        label="Absender:"
                        value={sender}
                        onChange={() => { }}
                        readOnly
                    />
                </div>

                <div className="modal-actions">
                    <button onClick={handleSend} className="primary-button">
                        Senden
                    </button>
                </div>
            </BaseModal>

            {isNewTemplateModalOpen && (
                <BaseModal
                    open={isNewTemplateModalOpen}
                    onClose={() => setIsNewTemplateModalOpen(false)}
                    onOpen={() => { }}
                    headerText="Neue Vorlage erstellen"
                    className="email-modal"
                    leftOffset={leftOffset + 500}
                >
                    <TextInput
                        label="Betreff:"
                        value={newTemplateSubject}
                        onChange={setNewTemplateSubject}
                        placeholder="Betreff der neuen Vorlage"
                    />
                    <TextArea
                        label="Inhalt:"
                        value={newTemplateBody}
                        onChange={setNewTemplateBody}
                        placeholder="Inhalt der neuen Vorlage"
                        minHeight={150}
                    />
                    <div className="modal-actions">
                        <button onClick={handleSaveNewTemplate} className="primary-button">
                            Speichern
                        </button>
                    </div>
                </BaseModal>
            )}
        </>
    );
};

export default EmailModal;
